import { ExtractedFrame, TrimData } from './types.ts';

// ---------------------------------------------------------------------------
// Facing configuration (matches server frameExtractor.ts exactly)
// ---------------------------------------------------------------------------

const FACING_CONFIGS: Array<{ facing: string; sourceRow: number; mirror: boolean }> = [
  { facing: 'south',     sourceRow: 0, mirror: false },
  { facing: 'southeast', sourceRow: 1, mirror: true  },
  { facing: 'east',      sourceRow: 2, mirror: true  },
  { facing: 'northeast', sourceRow: 3, mirror: true  },
  { facing: 'north',     sourceRow: 4, mirror: false },
  { facing: 'northwest', sourceRow: 3, mirror: false },
  { facing: 'west',      sourceRow: 2, mirror: false },
  { facing: 'southwest', sourceRow: 1, mirror: false },
];

function zeroPad(n: number): string {
  return String(n).padStart(2, '0');
}

// ---------------------------------------------------------------------------
// Trim helpers
// ---------------------------------------------------------------------------

interface TrimBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Scan pixel data for the bounding box of non-transparent pixels. Returns null if fully transparent. */
function findTrimBounds(
  ctx: OffscreenCanvasRenderingContext2D,
  width: number,
  height: number,
): TrimBounds | null {
  const { data } = ctx.getImageData(0, 0, width, height);
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 0) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  return maxX === -1 ? null : { minX, minY, maxX, maxY };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Extract all frames from a composited sprite sheet.
 * Produces 8 facings × columnCount frames. East-side facings are synthesized
 * by horizontally mirroring their west-side source row.
 * Matches the server's extractFramesFromSheet() output exactly.
 */
export async function extractFramesFromSheet(
  outputKey: string,
  sheet: ImageBitmap,
  columnCount: number,
  frameWidth: number,
  frameHeight: number,
): Promise<ExtractedFrame[]> {
  const frames: ExtractedFrame[] = [];

  for (const { facing, sourceRow, mirror } of FACING_CONFIGS) {
    for (let col = 0; col < columnCount; col++) {
      const srcX = col * frameWidth;
      const srcY = sourceRow * frameHeight;

      const frameCanvas = new OffscreenCanvas(frameWidth, frameHeight);
      const fCtx = frameCanvas.getContext('2d')!;

      if (mirror) {
        fCtx.save();
        fCtx.scale(-1, 1);
        fCtx.drawImage(sheet, srcX, srcY, frameWidth, frameHeight, -frameWidth, 0, frameWidth, frameHeight);
        fCtx.restore();
      } else {
        fCtx.drawImage(sheet, srcX, srcY, frameWidth, frameHeight, 0, 0, frameWidth, frameHeight);
      }

      const bounds = findTrimBounds(fCtx, frameWidth, frameHeight);

      let trim: TrimData;
      let bitmap: ImageBitmap;

      if (!bounds) {
        // Fully transparent — store a 1×1 placeholder
        trim = {
          trimmed: true,
          trimX: 0, trimY: 0, trimWidth: 1, trimHeight: 1,
          originalWidth: frameWidth, originalHeight: frameHeight,
        };
        bitmap = new OffscreenCanvas(1, 1).transferToImageBitmap();
      } else {
        const tw = bounds.maxX - bounds.minX + 1;
        const th = bounds.maxY - bounds.minY + 1;
        const noTrimNeeded = bounds.minX === 0 && bounds.minY === 0 && tw === frameWidth && th === frameHeight;

        if (noTrimNeeded) {
          trim = {
            trimmed: false,
            trimX: 0, trimY: 0, trimWidth: frameWidth, trimHeight: frameHeight,
            originalWidth: frameWidth, originalHeight: frameHeight,
          };
          bitmap = frameCanvas.transferToImageBitmap();
        } else {
          trim = {
            trimmed: true,
            trimX: bounds.minX, trimY: bounds.minY, trimWidth: tw, trimHeight: th,
            originalWidth: frameWidth, originalHeight: frameHeight,
          };
          const trimmedCanvas = new OffscreenCanvas(tw, th);
          const tCtx = trimmedCanvas.getContext('2d')!;
          tCtx.drawImage(frameCanvas, bounds.minX, bounds.minY, tw, th, 0, 0, tw, th);
          bitmap = trimmedCanvas.transferToImageBitmap();
        }
      }

      frames.push({
        name: `${outputKey}_${facing}_${zeroPad(col)}`,
        bitmap,
        width: trim.trimWidth,
        height: trim.trimHeight,
        trim,
      });
    }
  }

  return frames;
}
